import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { LoadingSpinner } from '../../components/general/LoadingSpinner';
import { FaSearch, FaTruck, FaArrowLeft } from 'react-icons/fa';

const VehicleModelListPage = () => {
  const navigate = useNavigate();
  const authToken = localStorage.getItem('authToken');
  
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  
  // Available filter options
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  const vehicleClasses = ['Light Duty', 'Medium Duty', 'Heavy Duty'];

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(API_ENDPOINTS.getVehicleModels, {
        headers: { Authorization: `Token ${authToken}` },
      });

      const vehicleData = response.data.vehicles || response.data || [];
      setVehicles(vehicleData);

      const makes = [...new Set(vehicleData.map(v => v.make).filter(Boolean))].sort();
      const years = [...new Set(vehicleData.map(v => v.model_year).filter(Boolean))].sort((a, b) => b - a);

      setAvailableMakes(makes);
      setAvailableYears(years);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError('Failed to load vehicle models. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Fetch vehicles
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedMake('');
    setSelectedClass('');
    setSelectedYear('');
  };

  // Filter vehicles based on current filters
  const filteredVehicles = vehicles.filter(vehicle => {
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        vehicle.make?.toLowerCase().includes(searchLower) ||
        vehicle.model?.toLowerCase().includes(searchLower) ||
        vehicle.category?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Make filter
    if (selectedMake && vehicle.make !== selectedMake) {
      return false;
    }

    // Class filter
    if (selectedClass && vehicle.vehicle_class !== selectedClass) {
      return false;
    }

    // Year filter
    if (selectedYear && vehicle.model_year !== parseInt(selectedYear)) {
      return false;
    }

    return true;
  });

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return `$${Number(value).toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => navigate('/data')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FaArrowLeft />
                <span>Back to DataHub</span>
              </button>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 font-ui mb-2">Vehicle Models Database</h1>
            <p className="text-gray-600 font-ui">Browse electric vehicle specifications and pricing</p>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <form onSubmit={handleSearch} className="space-y-4">
              {/* Search Bar */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search by make, model, or category..."
                      className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                  <select
                    value={selectedMake}
                    onChange={(e) => setSelectedMake(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Makes</option>
                    {availableMakes.map((make) => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Years</option>
                    {availableYears.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Class</label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Classes</option>
                    {vehicleClasses.map((cls) => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Results Section */}
          {loading ? (
            <LoadingSpinner message="Loading vehicle models..." />
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <FaTruck className="text-6xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-ui text-lg">No vehicle models found matching your criteria.</p>
              <button
                onClick={handleReset}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {/* Results Count */}
              <div className="mb-4">
                <p className="text-gray-600 font-ui">
                  Showing {filteredVehicles.length} of {vehicles.length} vehicle models
                </p>
              </div>

              {/* Results Table */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Make & Model
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Year
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Class
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Battery (kWh)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Range (mi)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Max Charging (kW)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Price
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredVehicles.map((vehicle) => (
                        <tr
                          key={vehicle.vehicle_id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                            {vehicle.make} {vehicle.model}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {vehicle.model_year || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {vehicle.vehicle_class}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {vehicle.batt_cap ? `${vehicle.batt_cap.toFixed(1)}` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {vehicle.operation_range ? `${vehicle.operation_range.toFixed(0)}` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {vehicle.max_charging_power ? `${vehicle.max_charging_power.toFixed(0)}` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                            {formatCurrency(vehicle.price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
    </div>
  );
};

export default VehicleModelListPage;
